import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { IAuthUser } from '../interfaces/user';
import { KeycloakSessionService } from '../services/auth/keycloak-session.service';

import { EmailStatus } from '../interfaces/keycloak-user';
import type { KeycloakUser } from '../interfaces/keycloak-user';

type AccountProfile = Pick<
  KeycloakUser,
  'id' | 'email' | 'firstName' | 'lastName' | 'attributes'
>;

function isAccountProfile(value: unknown): value is AccountProfile {
  if (typeof value !== 'object' || value === null || Array.isArray(value))
    return false;
  if (!('id' in value) || typeof value.id !== 'string' || !value.id)
    return false;
  for (const key of ['email', 'firstName', 'lastName']) {
    if (key in value && typeof value[key] !== 'string') return false;
  }
  if (!('attributes' in value)) return true;
  return (
    typeof value.attributes === 'object' &&
    value.attributes !== null &&
    !Array.isArray(value.attributes) &&
    Object.values(value.attributes).every(
      (items) =>
        Array.isArray(items) && items.every((item) => typeof item === 'string'),
    )
  );
}

export interface UserApplication {
  clientId: string;
  name: string;
  description: string;
  url: string;
}

function isUserApplication(value: unknown): value is UserApplication {
  return (
    typeof value === 'object' &&
    value !== null &&
    'clientId' in value &&
    typeof value.clientId === 'string' &&
    'name' in value &&
    typeof value.name === 'string' &&
    'description' in value &&
    typeof value.description === 'string' &&
    'url' in value &&
    typeof value.url === 'string'
  );
}

@Injectable({ providedIn: 'root' })
export class ProfileApiService {
  private readonly session = inject(KeycloakSessionService);
  private readonly http = inject(HttpClient);
  getProfile() {
    return this.http.get<unknown>(`${this.session.realmUrl}/account`).pipe(
      map((profile) => {
        if (!isAccountProfile(profile))
          throw new Error('Некорректный профиль пользователя IAM');
        return profile;
      }),
      map((profile): IAuthUser => ({
        id: profile.id,
        email: profile.email,
        fullName: [
          profile.lastName,
          profile.firstName,
          profile.attributes?.['middleName']?.[0],
        ]
          .filter(Boolean)
          .join(' '),
        phoneNumber: profile.attributes?.['phoneNumber']?.[0] ?? '',
        phoneNumberVerified:
          profile.attributes?.['phoneNumberVerified']?.[0] === 'true',
        isSecurityAdministrator: this.session.isSecurityAdministrator,
      })),
    );
  }
  getEmailStatus() {
    return this.http.get<EmailStatus>(
      `${this.session.iamUrl}/profile/email-status`,
    );
  }
  getApplications() {
    return this.http
      .get<unknown>(`${this.session.iamUrl}/profile/applications`)
      .pipe(
        map((value): UserApplication[] => {
          if (!Array.isArray(value) || !value.every(isUserApplication)) {
            throw new Error('Некорректный список назначенных подсистем');
          }
          return value;
        }),
      );
  }
}
